import { useEffect, useState } from 'react';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { timeAgo } from '@odo/shared-ui';
import { ConfirmModal } from '@odo/shared-ui/files/ConfirmModal';
import { ProtocolEditor, type ProtocolEditorFields } from '@odo/shared-ui/protocols/ProtocolEditor';
import {
    useCreateOrthodontistProtocol,
    useDeleteOrthodontistProtocol,
    useOrthodontistProtocols,
    useUpdateOrthodontistProtocol
} from '../../../../hooks/useOrthodontistProtocols';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import type { OrthodontistProtocol } from '../../../../services/orthodontistPreferencesProtocols';

const CARD_CLASSES =
    'bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-5 flex flex-col gap-5 shrink-0';

interface OrthodontistProtocolsCardProps {
    orthodontistId: string;
}

function useProtocolEditorActions(orthodontistId: string, onDone: () => void) {
    const { showToast } = useToast();
    const { mutateAsync: create, isPending: isCreating } =
        useCreateOrthodontistProtocol(orthodontistId);
    const { mutateAsync: update, isPending: isUpdating } =
        useUpdateOrthodontistProtocol(orthodontistId);
    const { mutateAsync: remove, isPending: isDeleting } =
        useDeleteOrthodontistProtocol(orthodontistId);

    const save = async (protocol: OrthodontistProtocol | null, fields: ProtocolEditorFields) => {
        try {
            if (protocol) {
                await update({ id: protocol.id, fields });
                showToast('Protocol updated', 'success');
            } else {
                await create(fields);
                showToast('Protocol created', 'success');
            }
            onDone();
        } catch (saveError) {
            showToast(apiErrorMessage(saveError, 'Failed to save protocol'), 'error');
        }
    };

    const deleteProtocol = async (protocol: OrthodontistProtocol) => {
        try {
            await remove(protocol.id);
            showToast('Protocol deleted', 'success');
            onDone();
        } catch (deleteError) {
            showToast(apiErrorMessage(deleteError, 'Failed to delete protocol'), 'error');
        }
    };

    return { save, deleteProtocol, isSaving: isCreating || isUpdating, isDeleting };
}

interface ProtocolListProps {
    protocols: OrthodontistProtocol[];
    isLoading: boolean;
    onSelect: (protocol: OrthodontistProtocol) => void;
}

function ProtocolList({ protocols, isLoading, onSelect }: ProtocolListProps) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }
    if (protocols.length === 0) {
        return <p className="text-sm text-gray-500 py-6 text-center">No protocols yet.</p>;
    }
    return (
        <div className="divide-y divide-gray-100">
            {protocols.map((protocol) => (
                <button
                    key={protocol.id}
                    type="button"
                    onClick={() => onSelect(protocol)}
                    className="w-full flex items-center justify-between gap-4 py-3 text-left hover:bg-gray-50 transition-colors"
                >
                    <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-medium text-gray-800 truncate">
                            {protocol.title}
                        </span>
                        <span className="text-xs text-gray-500">
                            Updated {timeAgo(protocol.updatedAt)}
                        </span>
                    </span>
                    <PencilIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                </button>
            ))}
        </div>
    );
}

export function OrthodontistProtocolsCard({ orthodontistId }: OrthodontistProtocolsCardProps) {
    const { showToast } = useToast();
    const { protocols, isLoading, isError, error } = useOrthodontistProtocols(orthodontistId);
    const [editing, setEditing] = useState<OrthodontistProtocol | 'new' | null>(null);
    const [pendingDelete, setPendingDelete] = useState<OrthodontistProtocol | null>(null);
    const actions = useProtocolEditorActions(orthodontistId, () => setEditing(null));

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load protocols'), 'error');
        }
    }, [isError, error, showToast]);

    if (editing !== null) {
        const protocol = editing === 'new' ? null : editing;
        return (
            <div className={CARD_CLASSES}>
                <ProtocolEditor
                    protocol={protocol}
                    isSaving={actions.isSaving}
                    isDeleting={actions.isDeleting}
                    onClose={() => setEditing(null)}
                    onSave={(fields) => void actions.save(protocol, fields)}
                    onDelete={protocol ? () => setPendingDelete(protocol) : undefined}
                />
                {pendingDelete && (
                    <ConfirmModal
                        title={`Delete "${pendingDelete.title}"?`}
                        message="It will no longer be shown on the org's patients."
                        confirmLabel="Delete"
                        destructive
                        onConfirm={() => {
                            setPendingDelete(null);
                            void actions.deleteProtocol(pendingDelete);
                        }}
                        onCancel={() => setPendingDelete(null)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={CARD_CLASSES}>
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-sm font-bold text-gray-800">Protocols</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Written protocols this org's team references, editable here for support.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setEditing('new')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors shrink-0"
                >
                    <PlusIcon className="w-3.5 h-3.5" />
                    New protocol
                </button>
            </div>
            <ProtocolList protocols={protocols} isLoading={isLoading} onSelect={setEditing} />
        </div>
    );
}
